import { LDateTime } from '@app/components/date/LDateTime';
import { User, UserRole } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { UseQueryResult } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { UserRoleInfo } from '../components/UserRoleInfo';
import { userRoleLabelMessages } from '../messages';

interface Props {
  query: UseQueryResult<{ results: User[] }, Error>;
}

export const UsersList = (props: Props) => {
  const { formatMessage } = useIntl();
  const { query } = props;

  return (
    <MrtTable
      navigateToOnClick={(id) => `/admin/users/${id}`}
      enableNavigateAction
      columns={[
        {
          header: formatMessage({
            id: 'admin.users.name',
            defaultMessage: 'Nombre',
          }),
          id: 'name',
          accessorFn: (row) => {
            const fullName = [row.name, row.surname].filter(Boolean).join(' ');
            return fullName || '\u200B';
          },
          size: 160,
          filterVariant: 'text',
        },
        {
          header: formatMessage({
            id: 'common.email',
            defaultMessage: 'Email',
          }),
          id: 'email',
          accessorKey: 'email',
          size: 150,
          filterVariant: 'text',
        },
        {
          header: formatMessage({
            id: 'admin.users.role',
            defaultMessage: 'Rol',
          }),
          id: 'role',
          accessorKey: 'role',
          Cell: ({ row: { original } }) => <UserRoleInfo value={original.role} display="short" />,
          size: 80,
          filterVariant: 'select',
          filterSelectOptions: [
            {
              label: formatMessage(userRoleLabelMessages[UserRole.CLIENT_USER]),
              value: UserRole.CLIENT_USER,
            },
            {
              label: formatMessage(userRoleLabelMessages[UserRole.CLIENT_ADMIN]),
              value: UserRole.CLIENT_ADMIN,
            },
          ],
        },
        {
          header: formatMessage({
            id: 'admin.users.lastLogin',
            defaultMessage: 'Último login',
          }),
          id: 'lastLogin',
          accessorKey: 'lastLogin',
          accessorFn: (row) => (row.lastLogin ? new Date(row.lastLogin) : ''),
          Cell: ({ cell }) => (cell.getValue() ? <LDateTime value={cell.getValue()} /> : ''),
          filterVariant: 'date-range',
          size: 200,
        },
      ]}
      data={query.data?.results ?? []}
      columnFilterDisplayMode="subheader"
      enableGlobalFilter
      defaultColumn={{
        enableSorting: true,
        enableColumnFilter: true,
        enableColumnActions: true,
      }}
      initialState={{
        sorting: [{ id: 'name', desc: false }],
      }}
      state={{
        showColumnFilters: true,
        showGlobalFilter: true,
        isLoading: query.isLoading,
      }}
    />
  );
};
